import ProformaInvoiceEntity, { PROFORMA_INVOICE_STATUS } from '../../db/entities/proforma_invoice/ProformaInvoiceEntity';

type DateOrder = 'asc' | 'desc';

/**
 * Define a priority for each status so we can sort in a predictable order:
 *   1. PENDING_PAYMENT
 *   2. PARTIALLY_PAID
 *   3. FULLY_PAID
 */
const STATUS_PRIORITY: Record<PROFORMA_INVOICE_STATUS, number> = {
    [PROFORMA_INVOICE_STATUS.PENDING_PAYMENT]: 1,
    [PROFORMA_INVOICE_STATUS.PARTIALLY_PAID]: 2,
    [PROFORMA_INVOICE_STATUS.FULLY_PAID]: 3,
};

/**
 * sortByStatus
 * — Returns a new array of invoices, sorted by status priority.
 * — If two invoices share the same status, they’re kept in original relative order.
 */
export const sortByStatus = (invoices: ProformaInvoiceEntity[]): ProformaInvoiceEntity[] => {
    return [...invoices].sort((a, b) => {
        const priorityA = STATUS_PRIORITY[a.status];
        const priorityB = STATUS_PRIORITY[b.status];
        return priorityA - priorityB;
    });
};

/**
 * filterByContactName
 * — Returns a new array of invoices whose contactName includes `searchTerm` (case-insensitive).
 */
export const filterByContactName = (
    invoices: ProformaInvoiceEntity[],
    searchTerm: string
): ProformaInvoiceEntity[] => {
    const lowerTerm = searchTerm.trim().toLowerCase();
    if (lowerTerm === '') {
        return [...invoices];
    }

    return invoices.filter((inv) => {
        const contactName = inv.contactName ?? '';
        return contactName.toLowerCase().includes(lowerTerm);
    });
};

/**
 * sortByCreatedDate
 * — Returns a new array sorted by `createdAt`.
 * — Invoices with `createdAt === null` are pushed to the end.
 * — `order = 'asc'` (oldest → newest), `order = 'desc'` (newest → oldest).
 */
export const sortByCreatedDate = (
    invoices: ProformaInvoiceEntity[],
    order: DateOrder = 'asc'
): ProformaInvoiceEntity[] => {
    return [...invoices].sort((a, b) => {
        const dateA = a.createdAt;
        const dateB = b.createdAt;

        // If both are null or equal
        if (dateA === dateB) return 0;

        // If one is null, push it to the end
        if (dateA === null) return 1;
        if (dateB === null) return -1;

        // Both are non-null numbers
        if (order === 'asc') {
            return dateA - dateB;
        } else {
            return dateB - dateA;
        }
    });
};

/**
 * sortByDueDate
 * — Returns a new array sorted by `dueDate`.
 * — Invoices with `dueDate === null` are pushed to the end.
 * — `order = 'asc'` (earliest → latest), `order = 'desc'` (latest → earliest).
 */
export const sortByDueDate = (
    invoices: ProformaInvoiceEntity[],
    order: DateOrder = 'asc'
): ProformaInvoiceEntity[] => {
    return [...invoices].sort((a, b) => {
        const dateA = a.dueDate;
        const dateB = b.dueDate;

        // If both are null or equal
        if (dateA === dateB) return 0;

        // If one is null, push it to the end
        if (dateA === null) return 1;
        if (dateB === null) return -1;

        // Both are non-null numbers
        if (order === 'asc') {
            return dateA - dateB;
        } else {
            return dateB - dateA;
        }
    });
};
